import { Navigate, useLocation } from 'react-router-dom';
import { Alert, Button, Container, Stack, Text, Title } from '@mantine/core';
import { useAuth } from '../auth/AuthContext';

/**
 * Общий возврат «маршрут не найден» — и заодно предохранитель от зацикливания.
 *
 * Наивный `<Navigate to={home} />` в этом месте опасен: если стартовый экран
 * роли не отрисован (нет прав) или сам равен «/», перенаправление ведёт
 * обратно сюда, и получается бесконечный круг. Выглядит это как пустая
 * страница — без ошибки в консоли, без единого запроса к серверу, без
 * единой подсказки, куда смотреть. Ровно так монтажник и не мог войти.
 *
 * Поэтому перенаправление происходит РОВНО ОДИН РАЗ: если мы уже стоим на
 * стартовом экране и всё равно оказались здесь, значит открыть его нечем —
 * и человеку показывается это словами, а не пустотой.
 */
export function HomeRedirect({ home }: { home: string }) {
  const location = useLocation();
  const { user, roles, logout } = useAuth();

  if (location.pathname !== home) {
    return <Navigate to={home} replace />;
  }

  return (
    <Container size="sm" py="xl">
      <Stack gap="md">
        <Title order={2}>Нечего показать</Title>

        <Alert color="orange" title="Разделы не открыты">
          <Text size="sm">
            {roles.length === 0
              ? `Учётной записи «${user?.fullName ?? ''}» не назначено ни одной роли.`
              : `Роли «${roles.join(', ')}» не дают доступа ни к одному разделу.`}
          </Text>
          <Text size="sm" mt="xs">
            Это настраивается в разделе «Сотрудники» — попросите руководителя
            или администратора назначить роль.
          </Text>
        </Alert>

        <Button variant="light" onClick={() => void logout()}>
          Выйти
        </Button>
      </Stack>
    </Container>
  );
}
